import { cn } from "@/lib/utils";
import { FileIcon, MessageSquareIcon } from "lucide-react";
import type { CodeSnippet } from "@/types/assessment";

interface FileTabsProps {
  files: CodeSnippet[];
  selectedIndex: number;
  onChange: (index: number) => void;
  commentCounts?: Record<string, number>; // filename -> count
  disabled?: boolean;
}

export function FileTabs({
  files,
  selectedIndex,
  onChange,
  commentCounts,
  disabled = false,
}: FileTabsProps) {
  if (files.length <= 1) {
    // Single file - just show filename without tabs
    return files.length === 1 ? (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground border-b bg-muted/30">
        <FileIcon className="size-4" />
        <span className="font-mono">{files[0].filename}</span>
      </div>
    ) : null;
  }

  return (
    <div className="flex items-center gap-1 border-b bg-muted/30 overflow-x-auto">
      {files.map((file, index) => {
        const isSelected = index === selectedIndex;
        const commentCount = commentCounts?.[file.filename] || 0;

        return (
          <button
            key={file.filename}
            onClick={() => !disabled && onChange(index)}
            disabled={disabled}
            className={cn(
              "flex items-center gap-2 px-4 py-2 text-sm font-mono border-b-2 transition-colors",
              "hover:bg-accent/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isSelected
                ? "border-primary bg-background text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <FileIcon className="size-3.5" />
            <span>{file.filename}</span>
            {commentCount > 0 && (
              <span
                className={cn(
                  "flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-full",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}
              >
                <MessageSquareIcon className="size-3" />
                {commentCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
